import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Crown, MessageCircle, Heart, Eye, Radio, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { Stars } from "@/components/games/GameCards";
import { PlatformBadge } from "@/components/community/StreamEmbed";

function Head({ eyebrow, title, to, label }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div><span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>{eyebrow}</span><h2 className="mt-1.5 text-2xl md:text-3xl font-bold text-white">{title}</h2></div>
      <Link to={to} data-testid={`${to.slice(1)}-see-more`} className="shrink-0 text-sm font-semibold text-white/60 hover:text-white inline-flex items-center gap-1">{label} <ArrowRight size={14} /></Link>
    </div>
  );
}

const score = (g) => (g.rating_count ? g.rating_avg.toFixed(1) : "–");

export function HomeTopGames() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/gamehub").then((r) => setItems(r.data.top.slice(0, 5))).catch(() => setItems([])); }, []);
  if (!items?.length) return null;
  const [first, ...rest] = items;
  return (
    <section data-testid="home-top-games">
      <Head eyebrow="Current status" title="Top 5 Games" to="/games" label="See more" />
      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-4">
        <Link to={`/games/${first.id}`} data-testid="home-top-game-1" className="group relative rounded-3xl overflow-hidden border border-yellow-400/40 min-h-[320px] lg:min-h-[420px] shadow-[0_30px_80px_-40px_rgba(250,204,21,0.6)]">
          <img src={mediaUrl(first.img)} alt={first.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <span className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold uppercase tracking-wider"><Crown size={14} /> #1 Top Game</span>
          <div className="absolute inset-x-0 bottom-0 p-6">
            <Stars value={first.rating_avg} size={18} />
            <h3 className="mt-2 text-3xl md:text-4xl font-bold text-white">{first.name}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-white/70">
              <span className="inline-flex items-center gap-1.5"><Star size={16} className="text-yellow-400 fill-yellow-400" /><b className="text-white font-head text-lg">{score(first)}</b>/10 · {first.rating_count} ratings</span>
              <span className="inline-flex items-center gap-1"><MessageCircle size={14} /> {first.comment_count}</span>
              <span className="text-white/50">{first.publisher}</span>
            </div>
          </div>
        </Link>
        <div className="grid grid-cols-2 gap-4">
          {rest.map((g) => (
            <Link key={g.id} to={`/games/${g.id}`} data-testid={`home-top-game-${g.rank}`} className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] lg:aspect-auto">
              <img src={mediaUrl(g.img)} alt={g.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <span className="absolute top-3 left-3 w-8 h-8 rounded-lg grid place-items-center bg-black/70 font-head font-bold text-white">{g.rank}</span>
              <div className="absolute inset-x-0 bottom-0 p-3">
                <div className="font-bold text-white truncate">{g.name}</div>
                <div className="text-xs text-white/70 inline-flex items-center gap-1"><Star size={12} className="text-yellow-400 fill-yellow-400" /> {score(g)}/10</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HomeTopStreamers() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/streams/top", { params: { limit: 5, live_only: false } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (items === null) return null;
  return (
    <section data-testid="home-top-streamers">
      <Head eyebrow="Community" title="Top Streamer" to="/community" label="See more" />
      {items.length === 0 ? <div className="card-surface rounded-2xl p-10 text-center text-white/50" data-testid="home-top-streamers-empty">No streamers yet — go live from your profile to be featured here.</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {items.map((s) => (
            <Link key={s.id} to={s.is_live ? `/community?stream=${s.id}` : "/community"} data-testid={`home-streamer-${s.rank}`} className="group card-surface rounded-2xl overflow-hidden hover-rise">
              <div className="relative aspect-video bg-black overflow-hidden">
                {s.streamer?.avatar ? <img src={mediaUrl(s.streamer.avatar)} alt="" className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${s.is_live ? "" : "opacity-50 grayscale"}`} />
                  : <div className="w-full h-full grid place-items-center text-white/15"><Radio size={28} /></div>}
                <div className="absolute top-2 left-2"><PlatformBadge platform={s.platform} /></div>
                <span className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${s.rank === 1 ? "bg-yellow-400 text-black" : "bg-black/70 text-white"}`}>#{s.rank}</span>
                {s.is_live && <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE</span>}
              </div>
              <div className="p-3">
                <div className="text-sm font-bold text-white truncate">{s.streamer?.name || "Streamer"}</div>
                <div className="text-xs text-white/40 truncate">{s.game || s.title}</div>
                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-white/60">
                  <span className="inline-flex items-center gap-1"><Heart size={11} className="text-red-400" /> {s.likes}</span>
                  {s.is_live && <span className="inline-flex items-center gap-1"><Eye size={11} /> {s.viewers}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
